import Vue from 'vue';
import VueRouter from 'vue-router';
import AppHome from '../views/Home.vue';
import AppImpressum from '../views/Impressum.vue';
import AppContact from '../views/Contact.vue';
import NotFound from '../views/NotFound.vue';

Vue.use(VueRouter);

const routes = [
  {
    path: '/',
    name: 'Home',
    component: AppHome,
    meta: {
      title: 'Jens Trompler IT Consulting | Digitalisierung, DevOps, Prozesse und Produkte',
      description: 'Jens Trompler IT Consulting verbindet Digitalisierung, DevOps, Prozessoptimierung, Produktentwicklung und eigene Softwareprodukte unter einer klaren operativen Struktur.',
      keywords: 'Jens Trompler IT Consulting, AI Advisory, DevOps, Prozessoptimierung, Digitalisierung, Automatisierung, JT Innovation, JT MVP Studio, JT AI Advisory, JT Medical Consulting, manaforgeai, JT Focus, Atlassian Archiv Suite'
    }
  },
  {
    path: '/impressum',
    name: 'Impressum',
    component: AppImpressum,
    meta: {
      title: 'Impressum | Jens Trompler IT Consulting',
      description: 'Impressum und rechtliche Informationen von Jens Trompler IT Consulting.',
      keywords: 'Impressum, Jens Trompler IT Consulting, Jens Trompler'
    }
  },
  {
    path: '/kontakt',
    name: 'Kontakt',
    component: AppContact,
    meta: {
      title: 'Kontakt | Jens Trompler IT Consulting',
      description: 'Kontakt für Anfragen zu Digitalisierung, DevOps, Prozessoptimierung, Produktentwicklung und den Marken von Jens Trompler IT Consulting.',
      keywords: 'Kontakt, Jens Trompler IT Consulting, AI Advisory, DevOps, Prozessoptimierung, JT Innovation, JT Medical Consulting'
    }
  },
  {
    path: '/danke',
    name: 'Danke',
    component: AppContact,
    meta: {
      title: 'Vielen Dank | Jens Trompler IT Consulting',
      description: 'Vielen Dank für Ihre Nachricht an Jens Trompler IT Consulting. Wir melden uns schnellstmöglich zurück.',
      keywords: 'Danke, Kontakt, Jens Trompler IT Consulting, Jens Trompler',
      showThankYouMessage: true
    }
  },
  { path: '/ki', redirect: '/#portfolio' },
  { path: '/devops', redirect: '/#model' },
  { path: '/services', redirect: '/#model' },
  { path: '/technology', redirect: '/#model' },
  { path: '/news', redirect: '/#about' },
  { path: '/preise', redirect: '/kontakt' },
  { path: '/intro', redirect: '/#about' },
  { path: '/development', redirect: '/#portfolio' },
  { path: '/views/Ki', redirect: '/#portfolio' },
  { path: '/views/DevOps', redirect: '/#model' },
  {
    path: '*',
    name: 'NotFound',
    component: NotFound,
    meta: {
      title: 'Seite nicht gefunden | Jens Trompler IT Consulting',
      description: 'Die angeforderte Seite wurde nicht gefunden. Zurück zu Jens Trompler IT Consulting.',
      keywords: '404, Seite nicht gefunden, Jens Trompler IT Consulting'
    }
  }
];

const defaultMeta = {
  title: 'Jens Trompler IT Consulting | Digitalisierung, DevOps, Prozesse und Produkte',
  description: 'Jens Trompler IT Consulting verbindet Digitalisierung, DevOps, Prozessoptimierung, Produktentwicklung und eigene Softwareprodukte unter einer klaren operativen Struktur.',
  keywords: 'Jens Trompler IT Consulting, AI Advisory, DevOps, Prozessoptimierung, Digitalisierung, Automatisierung, JT Innovation, JT MVP Studio, JT AI Advisory, JT Medical Consulting, manaforgeai, JT Focus, Atlassian Archiv Suite'
};

function setMetaTag(selector, attribute, value) {
  let metaTag = document.querySelector(selector);

  if (!metaTag) {
    metaTag = document.createElement('meta');

    if (selector.includes('name=')) {
      metaTag.setAttribute('name', selector.match(/name="([^"]+)"/)[1]);
    }

    if (selector.includes('property=')) {
      metaTag.setAttribute('property', selector.match(/property="([^"]+)"/)[1]);
    }

    document.head.appendChild(metaTag);
  }

  metaTag.setAttribute(attribute, value);
}

const router = new VueRouter({
  mode: 'hash',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const element = document.querySelector(to.hash);
          if (element) {
            const headerOffset = 80;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

            resolve({
              x: 0,
              y: offsetPosition,
              behavior: 'smooth'
            });
          } else {
            resolve({ x: 0, y: 0 });
          }
        }, 100);
      });
    }

    if (savedPosition) {
      return savedPosition;
    }

    return { x: 0, y: 0 };
  }
});

router.beforeEach((to, from, next) => {
  const nearestWithTitle = to.matched
    .slice()
    .reverse()
    .find((route) => route.meta && route.meta.title);

  if (nearestWithTitle) {
    document.title = nearestWithTitle.meta.title;
  } else {
    document.title = defaultMeta.title;
  }

  const nearestWithDescription = to.matched
    .slice()
    .reverse()
    .find((route) => route.meta && route.meta.description);

  if (nearestWithDescription) {
    setMetaTag('meta[name="description"]', 'content', nearestWithDescription.meta.description);
    setMetaTag('meta[property="og:description"]', 'content', nearestWithDescription.meta.description);
    setMetaTag('meta[property="twitter:description"]', 'content', nearestWithDescription.meta.description);
  } else {
    setMetaTag('meta[name="description"]', 'content', defaultMeta.description);
    setMetaTag('meta[property="og:description"]', 'content', defaultMeta.description);
    setMetaTag('meta[property="twitter:description"]', 'content', defaultMeta.description);
  }

  const nearestWithKeywords = to.matched
    .slice()
    .reverse()
    .find((route) => route.meta && route.meta.keywords);

  if (nearestWithKeywords) {
    setMetaTag('meta[name="keywords"]', 'content', nearestWithKeywords.meta.keywords);
  } else {
    setMetaTag('meta[name="keywords"]', 'content', defaultMeta.keywords);
  }

  setMetaTag('meta[name="title"]', 'content', document.title);
  setMetaTag('meta[property="og:title"]', 'content', document.title);
  setMetaTag('meta[property="twitter:title"]', 'content', document.title);

  next();
});

router.afterEach((to) => {
  if (to.hash) {
    Vue.nextTick(() => {
      const element = document.querySelector(to.hash);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    });
  }
});

export default router;
